// MergeFundsDialog.jsx — #57 BUILD item 3a: fund-variant merge, client-facing with undo.
// Non-destructive (founder decision 2): writes ONE merge_fund overlay per source stream under
// every non-surviving fund, pointing at the surviving fund's fund_key. The underlying streams
// never change — "Unmerge" just reverts those overlay rows.
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";
import { addOverlay, NOTE_MAX_LEN } from "./overlayApi";
import { collectFundStreamIds } from "./overlayGroups";

/**
 * @param {object[]} funds - selected entries [{ portalId, fund }], length >= 2
 */
export default function MergeFundsDialog({ open, onOpenChange, funds, token, onMerged }) {
  const confirm = useConfirm();
  const [survivorIdx, setSurvivorIdx] = useState(0);
  const [displayName, setDisplayName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  if (!funds || funds.length < 2) return null;

  const survivorName = (i) => funds[i].fund.fund_display_name || funds[i].fund.fund_name;
  const effectiveDisplayName = displayName.trim() || survivorName(survivorIdx);

  async function handleMerge(e) {
    e.preventDefault();
    const ok = await confirm({
      title: `Merge ${funds.length} funds into "${effectiveDisplayName}"?`,
      description:
        "The underlying documents and figures never change — you can undo this later from the merged fund's badge.",
      confirmText: "Merge",
      cancelText: "Cancel",
    });
    if (!ok) return;

    setBusy(true);
    try {
      const survivor = funds[survivorIdx];
      const targetFundKey = survivor.fund.fund_name; // canonical fund_slug identity
      const streamIds = funds
        .filter((_, i) => i !== survivorIdx)
        .flatMap((f) => collectFundStreamIds(f.fund));

      if (streamIds.length === 0) {
        toast.info("Nothing to merge — the other selected fund(s) have no positions.");
        setBusy(false);
        return;
      }

      await Promise.all(
        streamIds.map((sid) =>
          addOverlay({
            streamId: sid,
            kind: "merge_fund",
            payload: { target_fund_key: targetFundKey, display_name: effectiveDisplayName },
            note,
            token,
          })
        )
      );
      toast.success(`Merged ${funds.length} funds into "${effectiveDisplayName}"`);
      setNote("");
      onOpenChange(false);
      if (onMerged) onMerged();
    } catch (err) {
      toast.error("Merge failed: " + (err?.message || "error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="merge-funds-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold text-gray-900">
            Merge {funds.length} funds
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleMerge} className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1 block">
              Surviving fund
            </label>
            <div className="space-y-1">
              {funds.map((f, i) => (
                <label key={`${f.portalId}|${f.fund.fund_name}`} className="flex items-center gap-2 text-xs text-gray-700">
                  <input
                    type="radio"
                    name="merge-survivor"
                    checked={survivorIdx === i}
                    onChange={() => setSurvivorIdx(i)}
                    disabled={busy}
                  />
                  {survivorName(i)}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1 block">
              Display name
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={survivorName(survivorIdx)}
              disabled={busy}
              className="h-7 w-full px-2 rounded border border-gray-300 bg-white text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1 block">
              Note (optional)
            </label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX_LEN))}
              maxLength={NOTE_MAX_LEN}
              disabled={busy}
              className="text-xs min-h-[50px]"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={busy}
              className="h-8 px-3 rounded text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="h-8 px-3 rounded text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              data-testid="merge-funds-confirm"
            >
              {busy ? "Merging…" : "Merge funds"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
